// ── Shared image + file paste/drop → upload → inline markdown ref (C1403/C1447) ──
//
// Extracted from task-detail.js (was module-private there) so board.js's New Task
// modal can reuse it without a utils↔ui import cycle: ui.js already imports
// escapeHtml from utils.js, so this fn (which needs showToast from ui.js) can't
// live in utils.js itself. Same shared-helper convention as
// lib/task-key-reservation.js / lib/drag-order.js / lib/project.js.
//
// (C1447) Paste/drop now also accepts non-image files, routed to lib/file-attach.js's
// uploadAttachmentFile (task_files store) — the Embed ▾ menu (lib/embed-menu.js) shares
// the same upload paths so drag-drop, paste, and the picker all land identical refs.

import { uploadImage, insertAtCursor } from '../utils.js';
import { showToast } from '../ui.js';
import { uploadAttachmentFile } from './file-attach.js';

// `taskKey` may be a plain value OR a zero-arg fn returning the current value —
// the fn form lets a caller attach handlers before an async task_key reservation
// resolves (board.js's create-task modal) instead of leaving the textarea dead
// for the reservation round-trip. Existing callers passing a plain value/null are
// unaffected.

// uploadImageFile(textarea, file, {projectId, taskKey, onUpload}) — the image half of the
// upload pair, lifted out of attachImageHandlers' closure so lib/embed-menu.js's "Image"
// picker row can call it directly instead of duplicating the placeholder-swap logic.
// onUpload({kind:'images', url}) — optional, fired after a successful upload so a host can
// track exactly which attachments it created (board.js's New Task draft, C1512). Default
// no-op — existing callers (task-detail.js's 3 comment inputs + description editor) that
// don't pass it are unaffected.
export async function uploadImageFile(textarea, file, { projectId, taskKey = null, onUpload = null } = {}) {
  const placeholder = `![uploading ${file.name}...]()`;
  insertAtCursor(textarea, placeholder);
  try {
    const key = typeof taskKey === 'function' ? taskKey() : taskKey;
    const { url } = await uploadImage(projectId, file, key);
    if (!textarea.isConnected) return;
    textarea.value = textarea.value.replace(placeholder, `![${file.name}](${url})`);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    if (onUpload) onUpload({ kind: 'images', url });
  } catch (err) {
    if (textarea.isConnected) {
      textarea.value = textarea.value.replace(placeholder, '');
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    showToast(err.message || 'Upload failed', 'error');
  }
}

// attachImageHandlers(textarea, projectId, taskKey, opts) — opts.allowFiles (default
// false) extends paste/drop to also accept non-image files via uploadAttachmentFile.
// Left off by default: the 3 comment-input call sites (task-detail.js) keep today's
// image-only paste/drop; only the description editors (board.js's New Task modal,
// task-detail.js's startDescriptionEdit) opt in, since those are the two hosts that also
// render the Embed ▾ file picker (C1447) — dragging a PDF onto a field that advertises
// file support and having it silently do nothing would read as a bug.
// opts.onUpload({kind, url}) — optional, see uploadImageFile() above. Threaded through to
// both the image and file upload paths.
export function attachImageHandlers(textarea, projectId, taskKey = null, opts = {}) {
  const { allowFiles = false, onUpload = null } = opts;

  function uploadAndInsert(file) {
    return uploadImageFile(textarea, file, { projectId, taskKey, onUpload });
  }
  function uploadFileAndInsert(file) {
    return uploadAttachmentFile(textarea, file, { projectId, taskKey, onUpload });
  }

  textarea.addEventListener('paste', (e) => {
    const items = [...(e.clipboardData?.items || [])].filter(i => i.kind === 'file');
    if (!items.length) return;
    const imageFiles = items.filter(i => i.type.startsWith('image/')).map(i => i.getAsFile()).filter(Boolean);
    // Only preventDefault when there's actually a file item to handle — an ordinary text
    // paste that happens to carry a non-file clipboard item must never be hijacked.
    if (allowFiles) {
      const otherFiles = items.filter(i => !i.type.startsWith('image/')).map(i => i.getAsFile()).filter(Boolean);
      if (!imageFiles.length && !otherFiles.length) return;
      e.preventDefault();
      imageFiles.forEach(uploadAndInsert);
      otherFiles.forEach(uploadFileAndInsert);
    } else {
      if (!imageFiles.length) return;
      e.preventDefault();
      imageFiles.forEach(uploadAndInsert);
    }
  });
  textarea.addEventListener('dragover', (e) => {
    e.preventDefault();
    textarea.classList.add('img-drop-active');
  });
  textarea.addEventListener('dragleave', () => textarea.classList.remove('img-drop-active'));
  textarea.addEventListener('drop', (e) => {
    e.preventDefault();
    textarea.classList.remove('img-drop-active');
    const files = [...(e.dataTransfer?.files || [])];
    files.filter(f => f.type.startsWith('image/')).forEach(uploadAndInsert);
    if (allowFiles) {
      files.filter(f => !f.type.startsWith('image/')).forEach(uploadFileAndInsert);
    }
  });
}
